import { useOrderStatusUpdateFlow } from '../../hooks/orders/useOrderStatusUpdateFlow';
import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../components/CatalogListHeader/CatalogListHeader';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import OrderListViewMenu from '../../components/OrderListViewMenu/OrderListViewMenu';
import {
  ActionIcon,
  Button,
  Card,
  Drawer,
  Flex,
  Grid,
  Menu,
  Text,
  TextInput,
  Tooltip,
  rem
} from '@mantine/core';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IconFileSpreadsheet,
  IconMoneybag,
  IconTruckDelivery,
  IconWashMachine
} from '@tabler/icons-react';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import useDownloadExcel from '../../hooks/useDownloadExcel/useDownloadExcel';
import { formatMoneyString } from '../../utils/strings';
import {
  useGetOrders,
  useGetOrdersHeaderSummary
} from '../../hooks/orders/queries';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses from '../../enum/OrderStatuses';
import { useDisclosure } from '@mantine/hooks';
import OrderProductsModal from '../../components/OrderModals/OrderProductsModal';
import OrderPaymentLinesModal from '../../components/OrderModals/OrderPaymentLinesModal';
import { showNotification } from '../../utils/Notification/Notification';
import {
  useCreateOrderEvidence,
  useSendEmailReceipt,
  useSetOrderCancelled
} from '../../hooks/orders/mutations';
import { exportOrdersListV2 } from '../../services/order.service';
import { downloadTicketAsPNG, tryPrintTicket } from '../../utils/ticketHelper';
import { useSelector } from 'react-redux';
import { modals } from '@mantine/modals';
import OrderReadyByDateReport from '../../components/OrderModals/OrderReadyByDateReport';
import { MRT_PaginationState } from 'mantine-react-table';
import OrdersTableView from '../../components/ReadyOrdersViews/OrdersTableView';
import OrdersCardView from '../../components/ReadyOrdersViews/OrdersCardView';
import OrderEvidencesCarouselModal from '../../components/OrderModals/OrderEvidencesCarouselModal';
import UploadOrderEvidenceFile from '../NewOrder/components/UploadOrderEvidenceFile';
import { useGetStoreById } from '../../hooks/stores/queries';
import PaymentLinesByDateReport from '../../components/OrderModals/PaymentLinesByDateReport';
import ScheduledDeliveriesByDateReport from '../../components/OrderModals/ScheduledDeliveriesByDateReport';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { MercadoPagoPaymentSheet } from '../../components/MercadoPagoPaymentSheet';
import useCanUseMercadoPago from '../../hooks/useCanUseMercadoPago';
import { useOrderPaymentLinesModal } from '../../hooks/useOrderPaymentLinesModal';
import { useOrderCollectionActions } from '../../hooks/orders/useOrderCollectionActions';

const ReadyOrders = () => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'ReadyPage',
    'UpdateOrderForm',
    'ReportsPage',
    'OrdersHeaderSummary'
  ]);
  const { company, user } = useSelector((state) => state.user);
  const hideOrderTotals = user?.permission['hideOrderTotals']?.canView;
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const searchTermInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const orderStatusFilter = OrderStatuses.READY;
  const [showTableView, setShowTableView] = useState(false);

  const setSearchParamsDebounced = debounce((val: string) => {
    setSearchTerm(val || undefined);
  }, 500);
  const queryClient = useQueryClient();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [openedOrderProductsModal, orderProductsModalhandlers] = useDisclosure(false);
  const [openedOrderEvidenceModal, evidenceModalhandlers] = useDisclosure(false);
  const [openedScheduledDeliveriesDrawer, scheduledDeliveriesDrawerHandlers] = useDisclosure(false);
  const [scheduledDeliveriesDate, setScheduledDeliveriesDate] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
  const sendEmailReceipt = useSendEmailReceipt();
  const setOrderCancelled = useSetOrderCancelled();
  const createOrderEvidence = useCreateOrderEvidence();

  const limitPerReq = 9;
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch: refetchList,
    isLoading,
    isError
  } = useGetOrders(!!searchParamStoreId, {
    pageNum: showTableView ? pagination.pageIndex : undefined,
    q: searchTerm,
    status: orderStatusFilter,
    storeId: searchParamStoreId,
    limit: limitPerReq,
    fetchSection: 'true',
    includeAmountDueTotal: showTableView
  });

  const { data: store } = useGetStoreById(!!searchParamStoreId, searchParamStoreId);
  const { storeCanUseMP } = useCanUseMercadoPago(searchParamStoreId);
  const collection = useOrderCollectionActions({ store, company });
  const { onAskForPin } = collection;
  const onOrderCollected = async (...params: Parameters<typeof collection.onOrderCollected>) => {
    await collection.onOrderCollected(...params);
  };
  const onOrderPayAndCollect = async (...params: Parameters<typeof collection.onOrderPayAndCollect>) => {
    await collection.onOrderPayAndCollect(...params);
  };
  const onMercadoPagoSelected: typeof collection.onMercadoPagoSelected = selection => {
    setSelectedOrder(selection.order);
    collection.onMercadoPagoSelected(selection);
  };
  const paymentLinesModal = useOrderPaymentLinesModal({ store });

  const {
    data: summaryData,
    refetch: refetchSummary,
    isLoading: isLoadingSummary
  } = useGetOrdersHeaderSummary({
    status: orderStatusFilter,
    store: searchParamStoreId
  });

  const statusUpdate = useOrderStatusUpdateFlow({
    storeId: searchParamStoreId,
    status: 'ready',
    store,
    contextKey: searchTerm,
    onUpdated: async () => { await Promise.all([refetchList(), refetchSummary()]); }
  });

  const orders = useMemo(() => {
    return data?.pages?.reduce((list, { orders }: { orders: IOrder[] }) => {
      list.push(...orders);
      return list;
    }, [] as IOrder[]);
  }, [data]);

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows[0]?.totalRowsCount || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);
  const amountDueTotal = useMemo(() => data?.pages?.[0]?.amountDueTotal, [data]);

  const showOrderProductsModal = (order: IOrder) => {
    setSelectedOrder(order);
    orderProductsModalhandlers.open();
  };

  const showPaymentLinesModal = async (order: IOrder) => {
    setSelectedOrder(order);
    await paymentLinesModal.open(order);
  };

  const onAddPaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {}
  };

  const onDeletePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onUpdatePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onSendEmailReceipt = async (orderId: string) => {
    try {
      if (sendEmailReceipt.isPending) return;
      await sendEmailReceipt.mutateAsync(orderId);
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const clearSearchTerm = () => {
    if (searchTermInputRef.current) {
      searchTermInputRef.current.value = ''; // Reset the input field value via ref
      setSearchParamsDebounced(undefined); // Reset the state to trigger re-render
    }
  };

  const onPrintTicket = async (order: IOrder) => {
    try {
      tryPrintTicket(order, order.createdIn, t, 'ticketStructure', company.logoUrl, false, company?.companyTaxInfo);
    } catch (e) {}
  };

  const onDownloadPNGTicket = async (order: IOrder) => {
    try {
      downloadTicketAsPNG(order, order.createdIn, t, 'ticketStructure', company.logoUrl);
    } catch (e) {
      console.log('[ERROR] onDownloadPNGTicket:', e);
    }
  };

  const showOrderEvidenceModal = (order: IOrder) => {
    setSelectedOrder(order);
    evidenceModalhandlers.open();
  };

  const handleEvidenceDeleted = (evidenceId: string) => {
    setSelectedOrder((prev) =>
      prev
        ? {
            ...prev,
            evidence: prev.evidence?.filter((evidence) => evidence._id !== evidenceId) ?? []
          }
        : prev
    );
  };

  const onCreateOrderEvidence = async (orderId: string, data: FormData) => {
    try {
      if (createOrderEvidence.isPending) return;
      await createOrderEvidence.mutateAsync({ orderId, data });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      modals.close('uploadEvidenceModal');
      await refetchList();
      return true;
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      return false;
    }
  };

  const onShowCreateOrderEvidenceModal = (order: IOrder) => {
    modals.open({
      size: 'xl',
      modalId: 'uploadEvidenceModal',
      title: `${t('OrdersPage:evidenceModal.title')} - ${buildOrderFolio(
        order?.folioPrefix,
        order?.sequence
      )}`,
      children: (
        <>
          <UploadOrderEvidenceFile
            onSubmit={async (values: any, formData: any) => {
              return (await onCreateOrderEvidence(order._id || '', formData)) || false;
            }}
          />
        </>
      )
    });
  };

  const onOrderCancelled = async ({
    orderId,
    sequence,
    storeId
  }: {
    orderId?: string;
    sequence: string;
    storeId: string;
  }) => {
    try {
      if (setOrderCancelled.isPending) return;
      await setOrderCancelled.mutateAsync({
        sequence: sequence,
        storeId: storeId,
        data: {
          orderId: orderId || undefined,
          cancelledDateTime: new Date().toISOString()
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onUpdateOrdersModal = statusUpdate.open;

  const onShowCollectedOrdersReport = async (dateStr: string) => {
    modals.open({
      size: '100%',
      modalId: 'collectedOrdersReport',
      title: `${t('ReportsPage:collectedOrders.title')} - ${moment(dateStr).format('DD/MM/YYYY')}`,
      children: (
        <>
          <OrderReadyByDateReport storeId={searchParamStoreId} date={dateStr} />
        </>
      )
    });
  };

  const onShowPaymentsByDayReport = async (dateStr: string) => {
    modals.open({
      size: '100%',
      modalId: 'paymentsByDayReport',
      title: `${t('OrdersPage:paymentsByDayModal.title')}`,
      children: (
        <>
          <PaymentLinesByDateReport
            close={() => modals.close('paymentsByDayReport')}
            storeId={searchParamStoreId}
            date={dateStr}
          />
        </>
      )
    });
  };

  const onShowScheduledDeliveriesReport = async (dateStr: string) => {
    setScheduledDeliveriesDate(dateStr);
    scheduledDeliveriesDrawerHandlers.open();
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['orders']
      });
    };
  }, []);

  useEffect(() => {
    if (data && selectedOrder) {
      const updatedOrder = orders.find((order) => order._id === selectedOrder._id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
        paymentLinesModal.syncOrder(updatedOrder);
      }
    }
  }, [data, selectedOrder?._id]);

  return (
    <>
      <MercadoPagoPaymentSheet {...collection.mercadoPagoSheetProps} />
      <OrderEvidencesCarouselModal
        title={`${t('OrdersPage:evidenceModal.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          evidenceModalhandlers.close();
        }}
        opened={openedOrderEvidenceModal}
        order={selectedOrder}
        onEvidenceDeleted={handleEvidenceDeleted}
      />
      <OrderPaymentLinesModal
        onAddPaymentLine={onAddPaymentLine}
        title={paymentLinesModal.title}
        close={paymentLinesModal.close}
        opened={paymentLinesModal.opened}
        order={paymentLinesModal.order}
        onDeletePaymentLine={onDeletePaymentLine}
        onUpdatePaymentLine={onUpdatePaymentLine}
        pinUser={paymentLinesModal.pinUser}
      />
      <OrderProductsModal
        title={`${t('OrdersPage:productsModal.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          orderProductsModalhandlers.close();
        }}
        opened={openedOrderProductsModal}
        order={selectedOrder}
      />
      <CatalogContainer pos="relative">
        <CatalogListHeader title={t('ReadyPage:title')} showCreateButton={false} />
        <LoaderOverlay
          visible={
            isLoading ||
            collection.isCollecting ||
            setOrderCancelled.isPending ||
            statusUpdate.isPending
          }
        />
        <Grid>
          <Grid.Col span={12} my={10}>
            <Grid align="center" justify="center">
              <Grid.Col span={{ xs: 12, md: 'auto' }}>
                <TextInput
                  ref={searchTermInputRef}
                  onChange={(event) => setSearchParamsDebounced(event.currentTarget.value)}
                  placeholder={`${t('FieldName:order')}, ${t('FieldName:customer')}, ${t(
                    'FieldName:phone'
                  )}`}
                  label={`${t('FieldName:order')}`}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
                <Button fullWidth type="button" color="gray" onClick={clearSearchTerm}>
                  {t('CommonButtons:clear').toUpperCase()}
                </Button>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
                <Flex justify="flex-end" align="center" gap="xs" wrap="wrap">
                  <Tooltip withinPortal={false} label={t('CommonButtons:exportList')}>
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        downloadExcelV2(async () => {
                          try {
                            return await exportOrdersListV2({
                              q: searchTerm,
                              status: orderStatusFilter,
                              storeId: searchParamStoreId
                            });
                          } catch (e) {
                            throw e;
                          }
                        }, `Ordenes_${searchParamStoreId}`);
                      }}
                      variant="filled"
                      color="#1D6F42"
                      aria-label="Descargar_Listado_CSV_Ordenes">
                      <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <OrderListViewMenu
                    showTableView={showTableView}
                    onShowTableViewChange={setShowTableView}
                    storeId={searchParamStoreId}
                    user={user}
                    disabled={loadingExport}
                  />
                </Flex>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>

        <Grid
          pos={'sticky'}
          top={'calc(var(--app-shell-header-offset, 0rem) + var(--app-shell-padding))'}
          justify="center"
          align="center"
          style={{
            zIndex: 100
          }}>
          <Grid.Col span={12} pt={0}>
            <Card mt={0}>
              <Grid gutter={'md'}>
                <Grid.Col span={{ xs: 12, md: 4 }}>
                  <Grid>
                    <Grid.Col span={6}>
                      <Button fullWidth onClick={onUpdateOrdersModal} type="button">
                        Actualizar Pedido
                      </Button>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Menu shadow="md" width={200}>
                        <Menu.Target>
                          <Button fullWidth>Reportes</Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label>Reportes</Menu.Label>
                          <Menu.Item
                            onClick={() =>
                              onShowCollectedOrdersReport(moment().startOf('day').toISOString())
                            }
                            leftSection={
                              <IconWashMachine style={{ width: rem(14), height: rem(14) }} />
                            }>
                            Entregado Hoy
                          </Menu.Item>
                          <Menu.Item
                            onClick={() =>
                              onShowCollectedOrdersReport(
                                moment().add('day', -1).startOf('day').toISOString()
                              )
                            }
                            leftSection={
                              <IconWashMachine style={{ width: rem(14), height: rem(14) }} />
                            }>
                            Entregado Ayer
                          </Menu.Item>
                          {!hideOrderTotals && (
                            <Menu.Item
                              onClick={() =>
                                onShowPaymentsByDayReport(moment().startOf('day').toISOString())
                              }
                              leftSection={
                                <IconMoneybag style={{ width: rem(14), height: rem(14) }} />
                              }>
                              {t('OrdersPage:paymentsByDayModal.tooltip')}
                            </Menu.Item>
                          )}
                          <Menu.Item
                            onClick={() =>
                              onShowScheduledDeliveriesReport(moment().startOf('day').toISOString())
                            }
                            leftSection={
                              <IconTruckDelivery style={{ width: rem(14), height: rem(14) }} />
                            }>
                            {t('ReportsPage:scheduledDeliveriesReport.title')}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 8 }}>
                  <Grid justify="left" align="center">
                    <Grid.Col span={hideOrderTotals ? 3 : 3}>
                      <Text fw={'bold'}>
                        {t('OrdersHeaderSummary:orders')}: {summaryData?.ordersTotal}
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={hideOrderTotals ? 3 : 3}>
                      <Text fw={'bold'}>
                        {t('OrdersHeaderSummary:pieces')}:{' '}
                        {(summaryData?.totalPieces || 0).toFixed(2)}
                      </Text>
                    </Grid.Col>
                    {!hideOrderTotals && (
                      <>
                        <Grid.Col span={3}>
                          <Text fw={'bold'}>
                            {t('OrdersHeaderSummary:total')}:{' '}
                            {formatMoneyString((summaryData?.total || 0).toFixed(2))}
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <Text fw={'bold'}>
                            {t('OrdersHeaderSummary:amountDue')}:{' '}
                            {formatMoneyString((summaryData?.unpaidTotal || 0).toFixed(2))}
                          </Text>
                        </Grid.Col>
                      </>
                    )}
                  </Grid>
                </Grid.Col>
              </Grid>
            </Card>
          </Grid.Col>
        </Grid>
        {showTableView && (
          <OrdersTableView
            orders={orders}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isLoading}
            pagination={pagination}
            setPagination={setPagination}
            searchParamStoreId={searchParamStoreId}
            onOrderCollected={onOrderCollected}
            onOrderPayAndCollect={onOrderPayAndCollect}
            onMercadoPagoSelected={onMercadoPagoSelected}
            allowMercadoPago={storeCanUseMP}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onShowCreateOrderEvidenceModal={onShowCreateOrderEvidenceModal}
            onShowOrderEvidenceModal={showOrderEvidenceModal}
            onAskForPin={onAskForPin}
            amountDueTotal={amountDueTotal}
          />
        )}
        {!showTableView && (
          <OrdersCardView
            orders={orders}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            searchParamStoreId={searchParamStoreId}
            onOrderCollected={onOrderCollected}
            onOrderPayAndCollect={onOrderPayAndCollect}
            onMercadoPagoSelected={onMercadoPagoSelected}
            allowMercadoPago={storeCanUseMP}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            onDownloadPNGTicket={onDownloadPNGTicket}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onShowCreateOrderEvidenceModal={onShowCreateOrderEvidenceModal}
            onShowOrderEvidenceModal={showOrderEvidenceModal}
            onAskForPin={onAskForPin}
          />
        )}
      </CatalogContainer>
      <Drawer
        opened={openedScheduledDeliveriesDrawer}
        onClose={scheduledDeliveriesDrawerHandlers.close}
        title={t('ReportsPage:scheduledDeliveriesReport.title')}
        size="100%"
        position="right">
        <ScheduledDeliveriesByDateReport
          close={scheduledDeliveriesDrawerHandlers.close}
          storeId={searchParamStoreId || 'all'}
          companyId={company._id}
          date={scheduledDeliveriesDate}
        />
      </Drawer>
    </>
  );
};

export default ReadyOrders;
